import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { RemoveFromCartInput } from '@sg/graphql-schema';
import { useCartMutationErrorNotice } from '@sg/shared-fe/features/product-details';

import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { useNetworkErrorNotice } from '../../useNetworkErrorNotice';
import { useRemoveFromCartMutation } from '../GraphQL/Cart.generated';
import { useCart } from '../useCart';
import { useLogCartChangeCustomEvent } from '../useLogCartChangeCustomEvent';

// ────────────────────────────────────────────────────────────────────────────────

export const useRemoveLineItemFromCart = (
  props?: UseRemoveLineItemFromCartProps,
) => {
  const { formatMessage } = useIntl();
  const { onSuccess } = props ?? {};
  const { executeCartMutation, cart } = useCart();
  const logCartChangeCustomEvent = useLogCartChangeCustomEvent();

  const [removeFromCartResponse, removeLineItem] = useRemoveFromCartMutation();
  const { fetching, data } = removeFromCartResponse;

  useCartMutationErrorNotice(data?.removeFromCart);
  useNetworkErrorNotice(removeFromCartResponse);

  // ─── TELEMETRY ───────────────────────────────────────────────────

  const { track } = useTelemetry();

  const onRemoveFromBagFailedTelemetry = useCallback(
    (systemError?: string) => {
      track('bag.remove.failure', {
        userError: formatMessage(messages.removeLineItemError),
        systemError,
      });
    },
    [formatMessage, track],
  );

  // ─── HELPERS ─────────────────────────────────────────────────────

  const removeLineItemFromCart = useCallback(
    async (lineItemId: RemoveFromCartInput['lineItemId']) => {
      const productName = cart?.lineItems.find(
        (lineItem) => lineItem.id === lineItemId,
      )?.product.name;

      const executeMutation = async () =>
        removeLineItem({ input: { lineItemId } });
      const response = await executeCartMutation(
        executeMutation,
        'remove-line-item',
      );

      const responseData = response.data?.removeFromCart;
      const isSuccessfullyRemoved =
        responseData?.__typename === 'RemoveFromCartSuccess';
      const updatedCart = isSuccessfullyRemoved ? responseData.cart : null;

      if (!isSuccessfullyRemoved) {
        onRemoveFromBagFailedTelemetry(getUrqlError(response.error));

        return;
      }

      if (productName && updatedCart) {
        logCartChangeCustomEvent({
          action: 'REMOVE',
          cart: updatedCart,
          productName,
        });
      }

      onSuccess?.();
    },
    [
      cart?.lineItems,
      executeCartMutation,
      logCartChangeCustomEvent,
      onRemoveFromBagFailedTelemetry,
      onSuccess,
      removeLineItem,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({ removeLineItemFromCart, fetching }),
    [removeLineItemFromCart, fetching],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseRemoveLineItemFromCartProps = Readonly<{
  onSuccess?: () => void;
}>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  removeLineItemError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Remove Line Item From Cart | Error',
  },
});
